import React, { useEffect, useRef, useState } from 'react';
import PageContainer from '../../components/PageContainer';
import MainContent from '../../components/MainContent';
import PageTitle from '../../components/Common/PageTitle/PageTitle';
import ActionDropdownItem from '../../components/Common/PageTitle/ActionDropdownItem';
import { useConfig } from '../../DataStore/ConfigContext';
import printContent, { formatDate, numberFormat } from '../../util/araknerdlib';
import { useBalance } from '../../DataStore/BalanceContext';
import Pagination from '../../components/Common/Pagination';
import ActivityLoader from '../../components/Common/ActivityLoader';
import Notification from '../../components/Common/Notification';
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useTheme } from '../../DataStore/ThemeContext';
import MiniStatCard from '../../components/Banking/MiniStatCard';
import NewOpeningBalanceModal from '../../components/Period/NewOpeningBalanceModal';
import ConfirmationDialog from '../../components/Common/ConfirmationDialog';
import ajaxPeriod from '../../util/remote/ajaxPeriod';
import SplitOpeningBalanceModal from '../../components/Period/SplitOpeningBalanceModal';

const OpeningBalancesPage = () => {
    const {currentPeriod, getCurrentPeriod } = useConfig();
    const {openingBalances, getTransactions, setCurrentPage, metaData, mounted, getOpeningWorth, openingWorth} = useBalance();
    const statementRef = useRef(); // Ref to enclose statement content for printing
    const [selected, setSelected] = useState(null);
    const { setNotify } = useTheme();
    const [showNewBalanceModal, setShowNewBalanceModal] = useState(false);
    const [showDelete, setShowDelete] = useState(false);
    const [showSplitModal, setShowSplitModal] = useState(false);  
    
    
    useEffect(() => {
         if(!mounted){
             getTransactions();
             getOpeningWorth();
             getCurrentPeriod();
         }
      }, []);

    const refreshData = async() =>{
      await getTransactions(true);
      await getOpeningWorth(true);
    }
    
    const exportToExcel = (rows = []) => {
      if (!Array.isArray(rows) || rows.length === 0) {
        setNotify(
          <Notification
            type="info"
            message="No opening balances to export."
          />
        );
        return;
      }

      const formattedData = rows.map((transaction) => ({
        "Record Date": formatDate(transaction.created_at),
        "Transaction Date": formatDate(transaction.transaction_date),
        "Ledger No": transaction.ledger_no,
        "Payee": transaction.payee?.title || "-",
        "Account Type": transaction.account.account_type,
        "Debit": transaction.debit > 0 ? numberFormat(transaction.debit) : '',
        "Credit": transaction.credit > 0 ? numberFormat(transaction.credit) : '',
        "Description": transaction.description,
        "Account": transaction.account.account_name,
        "Tag": transaction.tag?.tag_name || "-",
        "Username": transaction.created_by.username
      }));

      const worksheet = XLSX.utils.json_to_sheet(formattedData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Opening Balances");
      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const dataBlob = new Blob([excelBuffer], { type: "application/octet-stream" });
      saveAs(dataBlob, `Opening_Balances_${currentPeriod ? `(${formatDate(currentPeriod.opening_date.db)} to ${formatDate(currentPeriod.closing_date.db)})` : ''}.xlsx`);
    };

    const handleDelete = () => {
      if(!selected){
        setNotify(<Notification type="danger" message="No opening balance selected for deletion." />);
        return;
      }
      setShowDelete(true);
    };

    const handleSplit = () => {
      if(!selected){
        setNotify(<Notification type="danger" message="No opening balance selected for splitting." />);
        return;
      }
      setShowSplitModal(true);
    };

    const confirmDelete = async() => {
      if(!selected){
        setNotify(<Notification type="danger" message="No opening balance selected for deletion." />);
        return;
      }

      const response = await ajaxPeriod.deleteOpeningBalance({ ledger_id: selected.core_grouping_id });
      if(response.status === "OK"){
        setNotify(<Notification type="success" message={response.message} />);
        refreshData();
        setSelected(null);
      } else {
        setNotify(<Notification type="danger" message={response.message} />);
      }
    };

    const updatedSplitData = async(newRecord) => {
      setSelected(newRecord);
      await refreshData();
    }
    
    const PageActions = () => (
    <>
      <ActionDropdownItem iconClass="fa fa-plus" label="Add Opening Balance" onClick={()=>setShowNewBalanceModal(true)} />
      <ActionDropdownItem iconClass="fa fa-columns" label="Split Balance" onClick={handleSplit} />
      <ActionDropdownItem iconClass="fa fa-file-excel" label="Export to Excel" onClick={() => exportToExcel(openingBalances)} />
      <ActionDropdownItem
        iconClass="fa fa-sync"
        label="Refresh"
        onClick={refreshData}
      />
      <ActionDropdownItem iconClass="fa fa-print text-secondary" label="Print Balances" onClick={()=>printContent(statementRef, "Opening Balances")}/>
      <ActionDropdownItem iconClass="fa fa-trash-alt" label="Delete Balance" className="text-danger" onClick={handleDelete}/>

    </>
  );


    return (
      <>
    <PageContainer>
      <MainContent>
        <PageTitle
          title="Opening Balances"
          description={`Showing all opening balances for financial period ${currentPeriod ? `(${ formatDate(currentPeriod?.opening_date.db)} to ${formatDate(currentPeriod?.closing_date.db)})` : ''}`}
          iconClass="fa fa-balance-scale"
          PageActions={PageActions}
          sticky={true}
          Pagination={
            metaData ? (
              <Pagination
                metaData={metaData}
                onPageChange={(page) => setCurrentPage(page)}
              />
            ) : null
          }
        />
         <div className="row mb-3">

              <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-building"
                        label="Assets"
                        value={openingWorth ? numberFormat(openingWorth.total_assets) : "00"}
                        tone="primary"
                        meta=""
                        hint="Opening balance for asset accounts."
                        loading={!openingWorth}
                    />
                </div>
                <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-briefcase"
                        label="Liabilities"
                        value={openingWorth ? numberFormat(openingWorth.total_liabilities) : "00"}
                        tone="danger"
                        meta=""
                        hint="Opening balance for liability accounts."
                        loading={!openingWorth}
                    />
                </div>
               
                <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-balance-scale"
                        label="Equity"
                        value={openingWorth ? numberFormat(openingWorth.total_equity) : "00"}
                        tone="warning"
                        meta=""
                        hint="Opening balance for equity accounts."
                        loading={!openingWorth}
                    />
                </div>
                <div className="col-md-3">
                    <MiniStatCard
                        icon="fa-equals"
                        label="Net Balance"
                        value={openingWorth ? numberFormat(openingWorth.net_balance) : "00"}
                        tone="success"
                        meta=""
                        hint="Net opening balance (Assets - Liabilities + Equity)."
                        loading={!openingWorth}
                    />
                </div>
          </div>
 

         <div className="card mb-3">
          <div className="card-body">
            {Array.isArray(openingBalances) && 
              <div className="table-responsive" ref={statementRef}>
                <table className="table table-hover table-striped table-bordered">
                  <thead>
                    <tr>
                      <th className="nowrap">Record Date</th>
                      <th className="nowrap">Trans Date</th>
                      <th>L/F</th>
                      <th>Payee</th>
                      <th>Ac. Type</th>
                      <th>Dr</th>
                      <th>Cr</th>
                      <th>Description</th>
                      <th>Account</th>
                      <th>Tag</th>
                      <th>Username</th>
                    </tr>
                  </thead>
                  <tbody>
                    {openingBalances.map((transaction, index) => (
                      <tr
                        key={index}
                        className={`link ${
                          transaction.core_grouping_id ===
                          selected?.core_grouping_id
                            ? "selected-row"
                            : ""
                        }`}
                        onClick={() => setSelected(transaction)}
                      >
                        <td className="nowrap">
                          {formatDate(transaction.created_at)}
                        </td>
                        <td>
                          {formatDate(transaction.transaction_date)}
                        </td>
                        <td>{transaction.ledger_no}</td>
                        <td>{transaction.payee?.title || "-"}</td>
                        <td>{transaction.account.account_type}</td>
                        <td>{transaction.debit > 0 ? numberFormat(transaction.debit) : ''}</td>
                        <td>{transaction.credit > 0 ? numberFormat(transaction.credit) : ''}</td>
                        <td>{transaction.description}</td>
                        <td>{transaction.account.account_name}</td>
                        <td>{transaction.tag?.tag_name || "-"}</td>
                        <td>{transaction.created_by.username}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            }

            {openingBalances === "404" &&
              <Notification
                type="info"
                message="No opening balances found for the current financial period."
                closable={false}
              />}
            {!openingBalances && <ActivityLoader width={20} />}
          </div>
        </div>

        </MainContent>
        </PageContainer>
        <NewOpeningBalanceModal
            show={showNewBalanceModal}
            onClose={()=>setShowNewBalanceModal(false)}
            onSuccess={()=>{
              refreshData();
            }}
        />
        {selected && <ConfirmationDialog
                title="Delete Opening Balance"
                alertDescription={`Are you sure you want to delete this opening balance, ${selected.ledger_no} for  ${selected.account.account_name} ?`}
                notes="Deleting this record might result into abnormal trial balance. Be sure to adjust the balances after deletion."
                buttonLabel="Delete Opening Balance"
                show={showDelete}
                handleClose={() => setShowDelete(false)}
                handleConfirm={confirmDelete}
           />}
        {selected && <SplitOpeningBalanceModal
                show={showSplitModal}
                onClose={() => setShowSplitModal(false)}
                onSuccess={updatedSplitData}
                record={selected}
           />}
        </>
    );
};

export default OpeningBalancesPage;